/**
 * Filling a script block's `{placeholder}` tokens — the ONE rule both renderers answer to.
 *
 * ⚠️ WHY THIS IS A SHARED MODULE AND NOT A LINE IN EACH RENDERER.
 *
 * A proposal is drawn twice from the same blocks: ModernTemplateEditor renders it into the
 * on-screen iframe, and ExportService renders it into the HTML that becomes the downloaded PDF.
 * Only the first substituted. The export emitted `block.content.html` exactly as stored, so
 * every token a template carried reached the file unresolved while the portal, beside it,
 * showed the real value.
 *
 * Reported on the proposal's last page: the broker's Email, Phone and Mobile read correctly in
 * the portal and were missing from the downloaded PDF — the one copy that is sent to the
 * client. The values were never absent; generation writes them to the block's
 * `placeholderValues` (see the server's consultant-profile injection) and the export simply
 * never looked.
 *
 * So the rule lives here and both callers use it. A renderer that draws a block without calling
 * this is the bug, and it is now one function to check rather than two to keep in step.
 */

/** Matches `{key}` and `{key:inline default}` — the spellings a template is authored with. */
const PLACEHOLDER = /\{([a-zA-Z_][a-zA-Z0-9_]*)(?::([^}]*))?\}/g;

/**
 * Resolve every placeholder in a block's HTML.
 *
 * Priority, unchanged from what the portal has always shown:
 *   1. the saved value — what generation injected, including the consultant's own details
 *   2. the inline default the template author wrote after the colon
 *   3. the token itself, so an unresolved placeholder is visible rather than silently blank
 */
export const substituteBlockPlaceholders = (
  html: string,
  values: Record<string, string> | null | undefined,
): string => {
  const saved = values ?? {};
  return html.replace(PLACEHOLDER, (_match, key: string, inlineDefault?: string) => {
    if (saved[key]) return saved[key];
    if (inlineDefault !== undefined) return inlineDefault.trim();
    return `{${key}}`;
  });
};
